import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, Link } from 'react-router-dom';
import { Copy, Check, Send, X } from 'lucide-react';
import { api } from '../api/client';
import type { ExecutionWithAction, ExecutionLogEntry } from '@databro/shared';
import {
  Card,
  CardContent,
  Button,
  Badge,
  StatusBadge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
} from '@databro/ui';

export default function Executions() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const actionIdFromUrl = searchParams.get('actionId');
  const actionNameFromUrl = searchParams.get('actionName');
  const clientIdFromUrl = searchParams.get('clientId');
  const clientNameFromUrl = searchParams.get('clientName');

  const [filters, setFilters] = useState<{
    actionId?: string;
    clientId?: string;
    status?: string;
    limit: number;
    offset: number;
  }>({
    actionId: actionIdFromUrl || undefined,
    clientId: clientIdFromUrl || undefined,
    limit: 20,
    offset: 0,
  });

  const [selectedExecution, setSelectedExecution] = useState<ExecutionWithAction | null>(
    null
  );

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Update filters when URL changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      actionId: actionIdFromUrl || undefined,
      clientId: clientIdFromUrl || undefined,
      offset: 0,
    }));
  }, [actionIdFromUrl, clientIdFromUrl]);

  const { data, isLoading } = useQuery({
    queryKey: ['executions', filters],
    queryFn: () => api.getExecutions(filters),
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => api.retryExecution(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['executions'] });
    },
  });

  const retryWebhooksMutation = useMutation({
    mutationFn: (executionIds: string[]) => api.retryWebhooks(executionIds),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['executions'] });
      setSelectedIds(new Set());
      // Show result via alert (could use toast in production)
      alert(`Webhooks queued: ${result.queued}, skipped: ${result.skipped}`);
    },
  });

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!data?.data) return;
    const allIds = data.data.map(e => e.id);
    const allSelected = allIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleRetryWebhooks = () => {
    if (selectedIds.size === 0) return;
    retryWebhooksMutation.mutate(Array.from(selectedIds));
  };

  const clearFilter = () => {
    setSearchParams({});
    setFilters(prev => ({ ...prev, actionId: undefined, clientId: undefined, offset: 0 }));
  };

  const hasFilter = actionIdFromUrl || clientIdFromUrl;
  const filterLabel = actionIdFromUrl
    ? `Action: ${actionNameFromUrl || 'Unknown'}`
    : clientIdFromUrl
    ? `Client: ${clientNameFromUrl || 'Unknown'}`
    : null;

  if (isLoading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Executions</h1>
          <p className="text-muted-foreground">
            {filterLabel
              ? `Filtered by ${filterLabel}`
              : 'History of action executions'
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          {hasFilter && (
            <Button variant="outline" size="sm" onClick={clearFilter}>
              Clear filter
            </Button>
          )}
          <Select
            value={filters.status || 'all'}
            onValueChange={(v: string) =>
              setFilters({ ...filters, status: v === 'all' ? undefined : v, offset: 0 })
            }
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="running">Running</SelectItem>
              <SelectItem value="queued">Queued</SelectItem>
              <SelectItem value="auth_required">Auth Required</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <Card className="bg-muted/50">
          <CardContent className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">
                {selectedIds.size} selected
              </span>
              <Button
                variant="default"
                size="sm"
                onClick={handleRetryWebhooks}
                disabled={retryWebhooksMutation.isPending}
              >
                <Send className="h-4 w-4 mr-2" />
                {retryWebhooksMutation.isPending ? 'Sending...' : 'Retry Webhooks'}
              </Button>
            </div>
            <Button variant="ghost" size="sm" onClick={clearSelection}>
              <X className="h-4 w-4 mr-1" />
              Clear Selection
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={data?.data && data.data.length > 0 && data.data.every(e => selectedIds.has(e.id))}
                  onCheckedChange={toggleSelectAll}
                />
              </TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Trigger</TableHead>
              <TableHead>Executor</TableHead>
              <TableHead>Time</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data?.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  {hasFilter ? 'No executions found for this filter' : 'No executions yet'}
                </TableCell>
              </TableRow>
            )}
            {data?.data.map((execution) => (
              <TableRow key={execution.id} className={selectedIds.has(execution.id) ? 'bg-muted/50' : ''}>
                <TableCell>
                  <Checkbox
                    checked={selectedIds.has(execution.id)}
                    onCheckedChange={() => toggleSelection(execution.id)}
                  />
                </TableCell>
                <TableCell>
                  <Link
                    to={`/executions?actionId=${execution.action.id}&actionName=${encodeURIComponent(execution.action.name)}`}
                    className="hover:underline"
                  >
                    <div className="font-medium">{execution.action.name}</div>
                  </Link>
                  <div className="text-xs text-muted-foreground">{execution.action.platform}</div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <StatusBadge status={execution.status} />
                    {execution.error && (
                      <div className="text-xs text-destructive max-w-xs truncate">
                        {execution.error}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {execution.trigger}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {execution.executor || '-'}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  <div>{new Date(execution.createdAt).toLocaleString()}</div>
                  {execution.completedAt && (
                    <div className="text-xs">
                      Duration:{' '}
                      {Math.round(
                        (new Date(execution.completedAt).getTime() -
                          new Date(execution.createdAt).getTime()) /
                          1000
                      )}
                      s
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedExecution(execution)}
                    >
                      Details
                    </Button>
                    {['failed', 'auth_required'].includes(execution.status) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => retryMutation.mutate(execution.id)}
                        disabled={retryMutation.isPending}
                      >
                        Retry
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {data && data.pagination.total > data.pagination.limit && (
          <div className="px-6 py-4 border-t flex justify-between items-center">
            <div className="text-sm text-muted-foreground">
              Showing {data.pagination.offset + 1} -{' '}
              {Math.min(
                data.pagination.offset + data.data.length,
                data.pagination.total
              )}{' '}
              of {data.pagination.total}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setFilters({
                    ...filters,
                    offset: Math.max(0, filters.offset - filters.limit),
                  })
                }
                disabled={filters.offset === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setFilters({ ...filters, offset: filters.offset + filters.limit })
                }
                disabled={!data.pagination.hasMore}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {selectedExecution && (
        <ExecutionDetailsModal
          execution={selectedExecution}
          onClose={() => setSelectedExecution(null)}
        />
      )}
    </div>
  );
}

function ExecutionDetailsModal({
  execution,
  onClose,
}: {
  execution: ExecutionWithAction;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopyResults = async () => {
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(execution.requestResults, null, 2)
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Execution Details</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-140px)] pr-2">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">Action</div>
                <div className="font-medium">{execution.action.name}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Status</div>
                <StatusBadge status={execution.status} />
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Trigger</div>
                <div>{execution.trigger}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Executor</div>
                <div>{execution.executor || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Started</div>
                <div>
                  {execution.startedAt
                    ? new Date(execution.startedAt).toLocaleString()
                    : '-'}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Completed</div>
                <div>
                  {execution.completedAt
                    ? new Date(execution.completedAt).toLocaleString()
                    : '-'}
                </div>
              </div>
            </div>

            {execution.error && (
              <div>
                <div className="text-sm text-muted-foreground mb-1">Error</div>
                <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
                  {execution.error}
                </div>
              </div>
            )}

            {execution.logs && (execution.logs as ExecutionLogEntry[]).length > 0 && (
              <div>
                <div className="text-sm text-muted-foreground mb-2">Execution Timeline</div>
                <LogsTimeline logs={execution.logs as ExecutionLogEntry[]} />
              </div>
            )}

            {execution.requestResults && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-muted-foreground">Request Results</div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleCopyResults}
                    title="Copy to clipboard"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <pre className="bg-muted p-4 rounded-md text-sm overflow-x-auto">
                  {JSON.stringify(execution.requestResults, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LogsTimeline({ logs }: { logs: ExecutionLogEntry[] }) {
  const getStageStyle = (stage: string) => {
    if (stage.includes('completed')) return { icon: '✓', variant: 'success' as const };
    if (stage.includes('failed')) return { icon: '✕', variant: 'destructive' as const };
    if (stage.includes('fallback')) return { icon: '↓', variant: 'warning' as const };
    if (stage.includes('started')) return { icon: '●', variant: 'default' as const };
    return { icon: '○', variant: 'secondary' as const };
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return null;
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    <Card>
      <CardContent className="py-4">
        <div className="relative">
          <div className="absolute left-3 top-4 bottom-4 w-0.5 bg-border" />

          <div className="space-y-3">
            {logs.map((log, index) => {
              const { icon, variant } = getStageStyle(log.stage);
              const isRequest = log.stage.includes('request');

              return (
                <div
                  key={index}
                  className={`relative flex items-start gap-3 ${isRequest ? 'ml-4' : ''}`}
                >
                  <div
                    className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      variant === 'success'
                        ? 'bg-green-500/15 text-green-500'
                        : variant === 'destructive'
                        ? 'bg-destructive/15 text-destructive'
                        : variant === 'warning'
                        ? 'bg-yellow-500/15 text-yellow-500'
                        : variant === 'default'
                        ? 'bg-primary/15 text-primary'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {icon}
                  </div>

                  <div className="flex-grow min-w-0 pt-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm">{log.message}</span>
                      {log.tier && (
                        <Badge variant="secondary" className="text-xs">
                          {log.tier}
                        </Badge>
                      )}
                      {log.duration !== undefined && (
                        <Badge variant="outline" className="text-xs">
                          {formatDuration(log.duration)}
                        </Badge>
                      )}
                      {log.status !== undefined && (
                        <Badge
                          variant={
                            log.status >= 200 && log.status < 300
                              ? 'success'
                              : log.status >= 400
                              ? 'destructive'
                              : 'secondary'
                          }
                          className="text-xs"
                        >
                          {log.status}
                        </Badge>
                      )}
                    </div>

                    {log.error && (
                      <div className="text-xs text-destructive mt-1">{log.error}</div>
                    )}

                    <div className="text-xs text-muted-foreground mt-0.5">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
